import { h } from '@/lib/dom';
import { Button } from '@/components/Button';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toast';
import { confirmDialog, openModal } from '@/components/Modal';
import { data } from '@/lib/data';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import { pageLayout } from './layout';
import { markdownSpan } from '@/lib/markdown-mini';
import type { AgendaItemOut, Call } from '@/api/types';

// Format YYYY-MM-DD → «22 мая 2026» (long, for nearest card)
function formatCallDateLong(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Format YYYY-MM-DD → «DD.MM.YYYY» (compact)
function formatCallDateCompact(iso: string): string {
  const [y, m, d] = iso.split('-') as [string, string, string];
  return `${d}.${m}.${y}`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function overdueDays(iso: string): number {
  const today = new Date(todayIso());
  const callDate = new Date(iso);
  return Math.round((today.getTime() - callDate.getTime()) / 86400000);
}

function isToday(iso: string): boolean {
  return iso === todayIso();
}

function openDatePickerModal(
  opts: { title: string; initialDate?: string },
  onSave: (date: string) => Promise<void>,
): void {
  openModal({
    title: opts.title,
    size: 'sm',
    content: (close) => {
      const input = h('input', {
        id: 'call-date-input',
        type: 'date',
        class: 'form-input',
        value: opts.initialDate ?? '',
      }) as HTMLInputElement;

      const saveBtn = h('button', {
        class: 'btn btn-primary',
        type: 'button',
        on: {
          click: async () => {
            if (!input.value) return;
            close();
            await onSave(input.value);
          },
        },
      }, 'Сохранить') as HTMLButtonElement;

      const cancelBtn = h('button', {
        class: 'btn btn-secondary',
        type: 'button',
        on: { click: () => close() },
      }, 'Отмена');

      // Disable save button when empty
      input.addEventListener('input', () => {
        saveBtn.disabled = !input.value;
      });
      saveBtn.disabled = !input.value;

      return h('div', null, [
        h('div', { class: 'form-field' }, [
          h('label', { class: 'form-label', for: 'call-date-input' }, 'Дата звонка'),
          input,
        ]),
        h('div', { class: 'modal-footer' }, [cancelBtn, saveBtn]),
      ]);
    },
  });
}

export async function renderAgenda(_ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }
  const isOwner = user.role === 'owner';

  const contentEl = h('div', { class: 'agenda-content' }, [
    Skeleton({ variant: 'rect', height: '200px' }),
    Skeleton({ variant: 'rect', height: '80px' }),
  ]);

  const newCallBtn = isOwner
    ? Button({
        label: '+ Новый звонок',
        variant: 'secondary',
        onClick: () => openDatePickerModal(
          { title: 'Новый звонок' },
          async (date) => {
            try {
              await data.createCall({ date });
              toast('Звонок запланирован', { type: 'success' });
              void refresh();
            } catch {
              toast('Не удалось создать звонок', { type: 'error' });
            }
          },
        ),
      })
    : null;

  const root = pageLayout({
    user,
    active: 'agenda',
    children: [
      h('div', { class: 'agenda-page' }, [
        h('div', { class: 'agenda-header' }, [
          h('div', { class: 'agenda-titles' }, [
            h('h1', { class: 'agenda-title' }, 'К обсуждению'),
          ]),
          newCallBtn,
        ]),
        contentEl,
      ]),
    ],
  });

  async function refresh(): Promise<void> {
    contentEl.innerHTML = '';
    try {
      const [activeCalls, archivedCalls] = await Promise.all([
        data.listCalls('active'),
        data.listCalls('archived'),
      ]);
      renderContent(activeCalls, archivedCalls);
      document.dispatchEvent(new Event('route:settled'));
    } catch {
      contentEl.appendChild(h('div', { class: 'empty-state', role: 'alert' }, [
        h('p', null, 'Не удалось загрузить звонки.'),
        Button({ label: 'Повторить', variant: 'secondary', onClick: () => void refresh() }),
      ]));
    }
  }

  function renderContent(activeCalls: Call[], archivedCalls: Call[]): void {
    contentEl.innerHTML = '';
    const sorted = [...activeCalls].sort((a, b) => a.date.localeCompare(b.date));
    const nearest = sorted[0] ?? null;
    const upcoming = sorted.slice(1);

    if (!nearest) {
      contentEl.appendChild(h('div', { class: 'empty-state' }, h('p', null, 'Звонков не запланировано')));
    } else {
      contentEl.appendChild(renderNearestSection(nearest));
      if (upcoming.length > 0) {
        contentEl.appendChild(renderUpcomingSection(upcoming));
      }
    }

    if (archivedCalls.length > 0) {
      contentEl.appendChild(renderArchiveSection(archivedCalls));
    }
  }

  function renderNearestSection(call: Call): HTMLElement {
    const section = h('div', null);
    section.appendChild(h('h2', { class: 'agenda-section-heading' }, 'Ближайший'));

    const overdue = !call.is_archived && call.date < todayIso();
    const today = isToday(call.date);
    const overdueDays_ = overdue ? overdueDays(call.date) : 0;

    const cardClasses = ['call-card', 'call-card--nearest', overdue ? 'call-card--overdue' : ''].filter(Boolean).join(' ');
    const card = h('div', { class: cardClasses });

    // Top row: date + badge + meta
    const dateEl = h('span', { class: `call-date${today ? ' call-date--today' : ''}` }, formatCallDateLong(call.date));
    const topChildren: (HTMLElement | null)[] = [dateEl];

    if (today) {
      topChildren.push(h('span', { class: 'call-badge-today' }, 'Сегодня'));
    } else if (overdue) {
      topChildren.push(h('span', { class: 'call-overdue-badge' }, [
        h('span', { 'aria-hidden': 'true' }, '⚠'),
        ` Просрочен ${overdueDays_} дн.`,
      ]));
    }

    topChildren.push(h('span', { class: 'call-card-meta' }, `${call.items_done} / ${call.items_total} тем`));
    card.appendChild(h('div', { class: 'call-card-top' }, topChildren.filter(Boolean) as HTMLElement[]));

    // Items list — loaded async
    const itemsEl = h('div', { class: 'call-item-list', role: 'list' });
    card.appendChild(itemsEl);
    void loadNearestItems(call, itemsEl);

    section.appendChild(card);
    return section;
  }

  async function loadNearestItems(call: Call, container: HTMLElement): Promise<void> {
    try {
      const detail = await data.getCall(call.id);
      container.innerHTML = '';

      if (isOwner) {
        container.appendChild(buildAddRow(call.id, () => void loadNearestItems(call, container)));
        container.appendChild(h('p', { class: 'agenda-add-hint' }, 'Можно: **жирный**, *курсив*, [ссылка](url)'));
      }

      if (detail.items.length === 0) {
        container.appendChild(h('div', { class: 'empty-state' }, 'Тем пока нет. Добавь первую.'));
      } else {
        const sorted = [...detail.items].sort((a, b) => a.sort_order - b.sort_order);
        sorted.forEach((item, idx) => {
          container.appendChild(agendaRow(item, sorted, idx, isOwner, () => void loadNearestItems(call, container)));
        });
      }

      // Notes section
      if (detail.notes) {
        const notesSection = h('div', { class: 'call-notes-section' }, [
          h('details', { class: 'call-notes-details' }, [
            h('summary', null, 'Заметки ▸'),
            h('div', { class: 'call-notes-body' }),
          ]),
        ]);
        const notesBody = notesSection.querySelector('.call-notes-body') as HTMLElement;
        notesBody.append(...Array.from(markdownSpan(detail.notes).childNodes));
        container.appendChild(notesSection);
      }

      // Footer
      const footerChildren: HTMLElement[] = [
        h('a', { href: `/agenda/${call.id}`, class: 'btn btn-ghost btn-sm' }, 'Открыть детальный вид →'),
      ];

      if (isOwner) {
        const changeDateBtn = Button({
          label: 'Изменить дату',
          variant: 'ghost',
          size: 'sm',
          onClick: () => openDatePickerModal(
            { title: 'Изменить дату', initialDate: call.date },
            async (date) => {
              try {
                await data.updateCall(call.id, { date });
                toast('Дата обновлена', { type: 'success' });
                void refresh();
              } catch {
                toast('Не удалось обновить дату', { type: 'error' });
              }
            },
          ),
        });
        const deleteBtn = Button({
          label: 'Удалить',
          variant: 'ghost',
          size: 'sm',
          onClick: async () => {
            const ok = await confirmDialog(
              'Все темы звонка будут удалены. Это действие нельзя отменить.',
              'Удалить звонок',
            );
            if (!ok) return;
            try {
              await data.deleteCall(call.id);
              toast('Звонок удалён', { type: 'success' });
              void refresh();
            } catch {
              toast('Не удалось удалить звонок', { type: 'error' });
            }
          },
        });
        deleteBtn.classList.add('call-delete-btn');
        footerChildren.push(changeDateBtn, deleteBtn);
      }

      container.appendChild(h('div', { class: 'call-card-footer' }, footerChildren));
    } catch {
      container.innerHTML = '';
      container.appendChild(h('p', { class: 'text-tertiary' }, 'Не удалось загрузить темы.'));
    }
  }

  function renderUpcomingSection(calls: Call[]): HTMLElement {
    const section = h('div', null);
    section.appendChild(h('h2', { class: 'agenda-section-heading' }, 'Следующие'));
    for (const call of calls) {
      const meta = `${call.items_total} тем`;
      const cardEl = h('div', { class: 'call-card' }, [
        h('div', { class: 'call-card-top' }, [
          h('span', { class: 'call-date' }, formatCallDateLong(call.date)),
          h('span', { class: 'call-card-meta' }, meta),
          h('div', { class: 'call-card-actions' }, [
            h('a', { href: `/agenda/${call.id}`, class: 'btn btn-ghost btn-sm' }, 'Открыть →'),
            isOwner
              ? (() => {
                  const btn = Button({
                    label: 'Удалить',
                    variant: 'ghost',
                    size: 'sm',
                    onClick: async () => {
                      const ok = await confirmDialog(
                        'Все темы звонка будут удалены. Это действие нельзя отменить.',
                        'Удалить звонок',
                      );
                      if (!ok) return;
                      try {
                        await data.deleteCall(call.id);
                        toast('Звонок удалён', { type: 'success' });
                        void refresh();
                      } catch {
                        toast('Не удалось удалить звонок', { type: 'error' });
                      }
                    },
                  });
                  btn.classList.add('call-delete-btn');
                  return btn;
                })()
              : null,
          ].filter(Boolean) as HTMLElement[]),
        ]),
      ]);
      section.appendChild(cardEl);
    }
    return section;
  }

  function renderArchiveSection(calls: Call[]): HTMLElement {
    const sorted = [...calls].sort((a, b) => a.date.localeCompare(b.date));
    const details = h('details', { class: 'agenda-archive-details' });
    const summary = h('summary', { class: 'agenda-archive-summary' }, [
      h('span', { class: 'agenda-archive-chevron' }, '▸'),
      ` Архив · ${sorted.length}`,
    ]);
    details.appendChild(summary);
    const list = h('div', { class: 'agenda-archive-list' });
    for (const call of sorted) {
      const row = h('div', { class: 'call-archive-row' }, [
        h('span', { class: 'call-archive-date' }, formatCallDateCompact(call.date)),
        h('span', { class: 'call-archive-meta' }, `${call.items_done} / ${call.items_total} тем`),
        h('a', { href: `/agenda/${call.id}`, class: 'btn btn-ghost btn-sm' }, '→'),
      ]);
      list.appendChild(row);
    }
    details.appendChild(list);
    return details;
  }

  void refresh();
  return root;
}

function buildAddRow(callId: number, refresh: () => void): HTMLElement {
  let inputEl: HTMLInputElement;
  const row = h('div', { class: 'agenda-add-row' }, [
    (inputEl = h('input', {
      class: 'form-input agenda-add-input',
      type: 'text',
      placeholder: 'Новая тема…',
      'aria-label': 'Новая тема',
      on: {
        keydown: (ev: KeyboardEvent) => {
          if (ev.key === 'Enter') void handleAdd();
        },
      },
    }) as HTMLInputElement),
    Button({
      label: '+ Добавить',
      variant: 'primary',
      size: 'sm',
      onClick: () => void handleAdd(),
    }),
  ]);

  async function handleAdd(): Promise<void> {
    const body = inputEl.value.trim();
    if (!body) return;
    const saved = inputEl.value;
    inputEl.value = '';
    try {
      await data.createCallItem(callId, { body });
      refresh();
    } catch {
      inputEl.value = saved;
      toast('Не удалось добавить тему', { type: 'error' });
    }
  }

  return row;
}

export function agendaRow(
  item: AgendaItemOut,
  allItems: AgendaItemOut[],
  idx: number,
  isOwner: boolean,
  refresh: () => void,
): HTMLElement {
  let editMode = false;
  let editTextarea: HTMLTextAreaElement | null = null;

  const bodySpan = h('span', { class: 'agenda-item-body' });
  bodySpan.append(...Array.from(markdownSpan(item.body).childNodes));

  function buildCheckbox(): HTMLElement {
    const cb = h('span', {
      class: `agenda-checkbox${item.is_done ? ' agenda-checkbox--checked' : ''}`,
      role: isOwner ? 'checkbox' : 'img',
      'aria-checked': isOwner ? String(item.is_done) : undefined,
      'aria-label': item.is_done ? 'Выполнено' : 'Не выполнено',
      tabindex: isOwner ? '0' : undefined,
    }, item.is_done ? '☑' : '☐');
    if (isOwner) {
      const toggle = async () => {
        try {
          await data.updateAgendaItem(item.id, { is_done: !item.is_done });
          refresh();
        } catch {
          toast('Не удалось обновить', { type: 'error' });
        }
      };
      cb.addEventListener('click', () => void toggle());
      cb.addEventListener('keydown', (ev: KeyboardEvent) => {
        if (ev.key === ' ' || ev.key === 'Enter') { ev.preventDefault(); void toggle(); }
      });
    }
    return cb;
  }

  const checkbox = buildCheckbox();

  const rowEl = h('div', {
    class: `agenda-item${item.is_done ? ' agenda-item--done' : ''}`,
    role: 'listitem',
  }, [
    checkbox,
    h('div', { class: 'agenda-item-content' }, bodySpan),
  ]);

  if (isOwner) {
    const actions = h('div', { class: 'agenda-item-actions' });

    const upBtn = Button({
      label: '↑',
      variant: 'ghost',
      size: 'sm',
      ariaLabel: 'Вверх',
      onClick: () => void handleReorder('up'),
    });
    if (idx === 0) (upBtn as HTMLButtonElement).disabled = true;

    const downBtn = Button({
      label: '↓',
      variant: 'ghost',
      size: 'sm',
      ariaLabel: 'Вниз',
      onClick: () => void handleReorder('down'),
    });
    if (idx === allItems.length - 1) (downBtn as HTMLButtonElement).disabled = true;

    const editBtn = Button({
      label: '✎',
      variant: 'ghost',
      size: 'sm',
      ariaLabel: 'Редактировать',
      onClick: () => toggleEdit(),
    });

    const deleteBtn = Button({
      label: '×',
      variant: 'ghost',
      size: 'sm',
      ariaLabel: 'Удалить',
      onClick: () => void handleDelete(),
    });
    deleteBtn.classList.add('agenda-delete-btn');

    actions.append(upBtn, downBtn, editBtn, deleteBtn);
    rowEl.appendChild(actions);
  }

  async function handleReorder(dir: 'up' | 'down'): Promise<void> {
    let newOrder: number;
    if (dir === 'up') {
      const before = allItems[idx - 2];
      const after = allItems[idx - 1]!;
      newOrder = before
        ? Math.round((before.sort_order + after.sort_order) / 2)
        : after.sort_order - 10;
    } else {
      const before = allItems[idx + 1]!;
      const after = allItems[idx + 2];
      newOrder = after
        ? Math.round((before.sort_order + after.sort_order) / 2)
        : before.sort_order + 10;
    }
    try {
      await data.reorderAgendaItem(item.id, { new_sort_order: newOrder });
      refresh();
    } catch {
      toast('Не удалось изменить порядок', { type: 'error' });
    }
  }

  async function handleDelete(): Promise<void> {
    const ok = await confirmDialog('Удалить тему?', 'Удаление');
    if (!ok) return;
    try {
      await data.deleteAgendaItem(item.id);
      refresh();
    } catch {
      toast('Не удалось удалить', { type: 'error' });
    }
  }

  function toggleEdit(): void {
    if (editMode) return cancelEdit();
    editMode = true;

    const contentEl = rowEl.querySelector('.agenda-item-content') as HTMLElement;
    contentEl.innerHTML = '';

    editTextarea = h('textarea', {
      class: 'form-textarea agenda-edit-textarea',
      'aria-label': 'Редактировать тему',
    }, item.body) as HTMLTextAreaElement;

    const saveBtn = Button({
      label: 'Сохранить',
      variant: 'primary',
      size: 'sm',
      onClick: () => void saveEdit(),
    });
    const cancelBtn = Button({
      label: '✕',
      variant: 'ghost',
      size: 'sm',
      ariaLabel: 'Отмена',
      onClick: () => cancelEdit(),
    });

    const editControls = h('div', { class: 'agenda-edit-controls' }, [
      h('span', { class: 'agenda-add-hint' }, 'Можно: **жирный**, *курсив*, [ссылка](url)'),
      h('div', { class: 'flex gap-2' }, [saveBtn, cancelBtn]),
    ]);

    contentEl.appendChild(editTextarea);
    contentEl.appendChild(editControls);

    editTextarea.focus();
    editTextarea.addEventListener('keydown', (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') cancelEdit();
      if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); void saveEdit(); }
      if (ev.key === 'Tab') { ev.preventDefault(); saveBtn.focus(); }
    });
  }

  function cancelEdit(): void {
    editMode = false;
    editTextarea = null;
    const contentEl = rowEl.querySelector('.agenda-item-content') as HTMLElement;
    contentEl.innerHTML = '';
    const newSpan = h('span', { class: 'agenda-item-body' });
    newSpan.append(...Array.from(markdownSpan(item.body).childNodes));
    contentEl.appendChild(newSpan);
  }

  async function saveEdit(): Promise<void> {
    if (!editTextarea) return;
    const body = editTextarea.value.trim();
    if (!body) return;
    try {
      await data.updateAgendaItem(item.id, { body });
      refresh();
    } catch {
      toast('Не удалось сохранить', { type: 'error' });
    }
  }

  return rowEl;
}
